import React, { useMemo, useState } from 'react';
import { Search, Check } from 'lucide-react';

const matches = (u, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  return [u.full_name, u.username, u.email, u.company_name].some((v) => (v || '').toLowerCase().includes(s));
};

export const UserMultiPicker = ({ users, selected, onChange, testIdPrefix = 'ump' }) => {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const filtered = useMemo(
    () => users.filter((u) => matches(u, q) && (role === 'all' || u.role === role)),
    [users, q, role],
  );
  const sel = new Set(selected);
  const toggle = (id) => onChange(sel.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const selectAll = () => onChange([...new Set([...selected, ...filtered.map((u) => u.id)])]);
  const clearFiltered = () => { const f = new Set(filtered.map((u) => u.id)); onChange(selected.filter((id) => !f.has(id))); };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, email o azienda (es. Microsoft)"
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg text-white text-sm px-2" data-testid={`${testIdPrefix}-role-filter`}>
          <option value="all">Tutti</option>
          <option value="client">Clienti</option>
          <option value="lead">Lead</option>
          <option value="collaborator">Collaboratori</option>
        </select>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-400" data-testid={`${testIdPrefix}-count`}>{selected.length} selezionati · {filtered.length} risultati</span>
        <span className="flex gap-3">
          <button type="button" onClick={selectAll} className="text-amber-400 hover:text-amber-300" data-testid={`${testIdPrefix}-select-all`}>Seleziona risultati</button>
          <button type="button" onClick={clearFiltered} className="text-slate-400 hover:text-white" data-testid={`${testIdPrefix}-clear`}>Deseleziona risultati</button>
        </span>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-700 divide-y divide-slate-800">
        {filtered.length === 0 && <p className="text-center text-sm text-slate-500 py-6">Nessun utente trovato</p>}
        {filtered.map((u) => (
          <button
            type="button" key={u.id} onClick={() => toggle(u.id)}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${sel.has(u.id) ? 'bg-amber-500/10' : 'hover:bg-slate-800'}`}
            data-testid={`${testIdPrefix}-user-${u.id}`}
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${sel.has(u.id) ? 'bg-amber-500 border-amber-500' : 'border-slate-500'}`}>
              {sel.has(u.id) && <Check className="w-3 h-3 text-slate-950" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm text-white truncate">{u.full_name || u.username}</span>
              <span className="block text-xs text-slate-500 truncate">{u.email || u.username}{u.company_name ? ` · ${u.company_name}` : ''}</span>
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${u.role === 'lead' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-blue-500/20 text-blue-300'}`}>
              {u.role === 'lead' ? 'Lead' : u.role === 'collaborator' ? 'Collab.' : 'Cliente'}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
